import React, { useEffect, useState } from 'react';
import { ISystemMail, systemMailSchema } from '../Interfaces/ISystemMail';
import { ISystemMail_has_Dictionary } from '../Interfaces/ISystemMail_has_Dictionary';
import {
  Alert,
  Box,
  Breakpoint,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useUpdateSystemMail } from '../hooks/reactQuerys/SystemMailQueries';
import { useAlert } from '../context/AlertContext';
import { CustomeTextField } from '../generic/CustomeTextField';
import { CustomEditor } from '../core/CustomEditor';
import { IDictionaryEntry } from '../Interfaces/IDictionaryEntry';
import { IDictionary } from '../Interfaces/IDictionary';
import { SystemMailDialogAdmin } from './Childs/SystemMailDialogAdmin';
import { ISystemMailCategory } from '../Interfaces/ISystemMailCategory';

interface IProps {
  idSystemMail: number;
  systemMails: ISystemMail[];
  systemMailCategories: ISystemMailCategory[];
  systemMailHasDictionaryArray: ISystemMail_has_Dictionary[];
  onClose: () => void;
  onSave: () => void;
  isAdmin: boolean;
  dictionaries: IDictionary[];
  dictionaryEntries: IDictionaryEntry[];
  refetchDictionary: () => void;
  refetchDictionaryEntry: () => void;
  refetchSystemMailHasDictionary: () => void;
}

export const SystemMailDialog: React.FC<IProps> = ({
  idSystemMail,
  systemMails,
  systemMailCategories,
  systemMailHasDictionaryArray,
  onClose,
  onSave,
  isAdmin,
  dictionaries,
  dictionaryEntries,
  refetchDictionary,
  refetchDictionaryEntry,
  refetchSystemMailHasDictionary,
}) => {
  const { showAlert } = useAlert();
  const size: Breakpoint = isAdmin ? 'xl' : 'lg';

  const { updateSystemMail, isPendingSystemMail } = useUpdateSystemMail();
  const [editSystemMail, setEditSystemMail] = useState(
    systemMails.find(sysMail => sysMail.idSystemMail === idSystemMail)
  );

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (editSystemMail) {
      try {
        let uploadObject = systemMailSchema.parse(editSystemMail);
        await updateSystemMail(uploadObject);
        showAlert({
          text: 'Erfolgreich gespeichert!',
          severity: 'success',
        });
        onSave();
      } catch (error) {
        console.error(error);
        showAlert({
          text: 'Es ist ein Fehler aufgetretten!',
          severity: 'error',
        });
      }
    }
  };

  if (!editSystemMail) {
    return (
      <Dialog open onClose={onClose} maxWidth={size} fullWidth>
        <DialogContent>
          <Alert severity='error'>
            Die Systemnachricht konnte nicht bezogen werden!
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button variant='contained' onClick={onClose}>
            Okay
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open onClose={onClose} maxWidth={size} fullWidth>
        <form onSubmit={handleSave}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={isAdmin ? 5 : 12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant='body2'>
                      Interne Informationen
                    </Typography>
                  </Grid>
                  {isAdmin && (
                    <Grid item xs={4}>
                      <CustomeTextField
                        label='Kategorie'
                        attr='idSystemMailCategory'
                        object={editSystemMail}
                        setObject={setEditSystemMail}
                        type='select'
                        required
                      >
                        {systemMailCategories.map(sysMailCat => (
                          <MenuItem
                            key={`select-idSystemMailCategory-${sysMailCat.idSystemMailCategory}`}
                            value={sysMailCat.idSystemMailCategory}
                          >
                            {sysMailCat.SystemMailCategory}
                          </MenuItem>
                        ))}
                      </CustomeTextField>
                    </Grid>
                  )}
                  <Grid item xs={isAdmin ? 8 : 12}>
                    <CustomeTextField
                      label='Titel'
                      attr='Title'
                      object={editSystemMail}
                      setObject={setEditSystemMail}
                      type='string'
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CustomeTextField
                      label='Infotext'
                      attr='InfoText'
                      object={editSystemMail}
                      setObject={setEditSystemMail}
                      type='string'
                      null
                      rows={3}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12}>
                    <Typography variant='body2'>E-Mail</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <CustomeTextField
                      label='Betreff'
                      attr='Subject'
                      object={editSystemMail}
                      setObject={setEditSystemMail}
                      type='string'
                      required
                      maxLength='md'
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CustomEditor
                      idTextModuleCategory={1}
                      txt={editSystemMail.Body}
                      setTxt={(txt: string) =>
                        setEditSystemMail({
                          ...editSystemMail,
                          Body: txt,
                        })
                      }
                      dictonaryIdsArray={systemMailHasDictionaryArray
                        .filter(x => x.idSystemMail === idSystemMail)
                        .map(x => x.idDictionary)}
                    />
                  </Grid>
                </Grid>
              </Grid>
              {isAdmin && (
                <SystemMailDialogAdmin
                  idSystemMail={idSystemMail}
                  dictionaries={dictionaries}
                  dictionaryEntries={dictionaryEntries}
                  systemMailHasDictionaryArray={systemMailHasDictionaryArray}
                  refetchDictionary={refetchDictionary}
                  refetchDictionaryEntry={refetchDictionaryEntry}
                  refetchSystemMailHasDictionary={
                    refetchSystemMailHasDictionary
                  }
                />
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button variant='outlined' onClick={onClose}>
              Abbruch
            </Button>
            <Button
              variant='contained'
              type='submit'
              disabled={isPendingSystemMail}
            >
              {isPendingSystemMail ? (
                <Box display='flex' alignItems='center' gap={1}>
                  <span>Speichern</span>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                'Speichern '
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};
